import {
  ReactNode,
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Snackbar, SnackbarCloseReason, Modal, Box, Typography, Fade } from "@mui/material";
import type { IceServers } from "../components/VideoPlayer";
import { alpha } from '@mui/material/styles';
import { useAuth } from "./AuthContext";
import type { PwaNotificationSettings } from "../types/pwaNotifications";
import { defaultPwaNotificationSettings, normalizePwaNotificationSettings } from "../types/pwaNotifications";
import type { StreamStateValue } from "../types/streamState";
import { canAccessProtectedApis } from "../utils/authAccess";

const serverConfigChangedEvent = "serverConfigChanged";
const clientCountChangedEvent = "clientCountChanged";
const streamStateSnapshotEvent = "streamStateSnapshot";
const streamStateChangedEvent = "streamStateChanged";
const streamOutputStateSnapshotEvent = "streamOutputStateSnapshot";
const streamOutputStateChangedEvent = "streamOutputStateChanged";
const streamDetectionEvent = "streamDetectionUpdated";
const streamListChangedEvent = "streamListChanged";
const platformListChangedEvent = "platformListChanged";
const notificationEvent = "notification";
const offlineMapProgressEvent = "offlineMapProgress";
const connectionInfoEvent = "connectionInfo";
const restartNotificationMessage = "Restarting the server...";

// Stream state message types
type StreamStateMessage = {
  type: typeof streamStateSnapshotEvent | typeof streamStateChangedEvent;
  data: unknown;
};

type StreamOutputStateMessage = {
  type: typeof streamOutputStateSnapshotEvent | typeof streamOutputStateChangedEvent;
  data: unknown;
};

// Stream detection message types
type StreamDetectionMessage = {
  type: typeof streamDetectionEvent;
  data: unknown;
};

// Stream list change message types
type StreamListChangeMessage = {
  type: typeof streamListChangedEvent;
  data: unknown;
};

// Platform list change message types
type PlatformListChangeMessage = {
  type: typeof platformListChangedEvent;
  data: unknown;
};

type NotificationMessage = {
  type: typeof notificationEvent;
  data: { message?: unknown };
};

type OfflineMapProgressMessage = {
  type: typeof offlineMapProgressEvent;
  data: { requestId?: unknown; progress?: unknown; status?: unknown; error?: unknown; operation?: unknown };
};

type StreamStateMessageHandler = (message: StreamStateMessage) => void;
type StreamOutputStateMessageHandler = (message: StreamOutputStateMessage) => void;
type StreamDetectionMessageHandler = (message: StreamDetectionMessage) => void;
type StreamListChangeMessageHandler = (message: StreamListChangeMessage) => void;
type PlatformListChangeMessageHandler = (message: PlatformListChangeMessage) => void;

// Global stream state message subscribers
const streamStateSubscribers = new Set<StreamStateMessageHandler>();
const streamOutputStateSubscribers = new Set<StreamOutputStateMessageHandler>();
const streamDetectionSubscribers = new Set<StreamDetectionMessageHandler>();
const streamListChangeSubscribers = new Set<StreamListChangeMessageHandler>();
const platformListChangeSubscribers = new Set<PlatformListChangeMessageHandler>();
const notificationSubscribers = new Set<(message: NotificationMessage) => void>();
const offlineMapProgressSubscribers = new Set<(message: OfflineMapProgressMessage) => void>();

export const subscribeToStreamStateMessages = (handler: StreamStateMessageHandler) => {
  streamStateSubscribers.add(handler);
  return () => {
    streamStateSubscribers.delete(handler);
  };
};

export const subscribeToStreamOutputStateMessages = (handler: StreamOutputStateMessageHandler) => {
  streamOutputStateSubscribers.add(handler);
  return () => {
    streamOutputStateSubscribers.delete(handler);
  };
};

export const subscribeToStreamDetectionMessages = (handler: StreamDetectionMessageHandler) => {
  streamDetectionSubscribers.add(handler);
  return () => {
    streamDetectionSubscribers.delete(handler);
  };
};

export const subscribeToStreamListChanges = (handler: StreamListChangeMessageHandler) => {
  streamListChangeSubscribers.add(handler);
  return () => {
    streamListChangeSubscribers.delete(handler);
  };
};

export const subscribeToPlatformListChanges = (handler: PlatformListChangeMessageHandler) => {
  platformListChangeSubscribers.add(handler);
  return () => {
    platformListChangeSubscribers.delete(handler);
  };
};

export const subscribeToNotifications = (handler: (message: NotificationMessage) => void) => {
  notificationSubscribers.add(handler);
  return () => {
    notificationSubscribers.delete(handler);
  };
};

export const subscribeToOfflineMapProgress = (handler: (message: OfflineMapProgressMessage) => void) => {
  offlineMapProgressSubscribers.add(handler);
  return () => {
    offlineMapProgressSubscribers.delete(handler);
  };
};

type ServerConfigPayload = {
  rtspPlaybackBaseUrl?: unknown;
  iceServers?: unknown;
  forceRelay?: unknown;
  fullIce?: unknown;
  keepHostCandidates?: unknown;
  loopbackIceCandidate?: unknown;
  nat1to1Enabled?: unknown;
  publicIp?: unknown;
  alwaysTranscodeH265ToH264?: unknown;
  enableWebRtcDebug?: unknown;
  userManagementEnabled?: unknown;
  userManagementDisabled?: unknown;
  errorDetectionEnabled?: unknown;
  analyticsEnabled?: unknown;
  enableSystemMonitor?: unknown;
  enablePreview?: unknown;
  recastLicensed?: unknown;
  recordLicensed?: unknown;
  rawPcapAvailable?: unknown;
  udsDatagramAvailable?: unknown;
  patternAvailable?:unknown;
 patternUnavailableReason?:unknown;
 flywheelAvailable?: unknown;
  flywheelLicensed?: unknown;
  flywheelUnavailableReason?: unknown;
  isDemoMode?: unknown;
  // UI notifications from backend
  licenseExpired?: unknown;
  demoExpired?: unknown;
  maxClientsExceeded?: unknown;
  mapTileLayers?: unknown;
};

export type ServerConfigState = {
  rtspPlaybackBaseUrl: string | null;
  iceServers: IceServers | null;
  forceRelay: boolean;
  fullIce: boolean;
  keepHostCandidates: boolean;
  loopbackIceCandidate: boolean;
  nat1to1Enabled: boolean;
  publicIp: string | null;
  alwaysTranscodeH265ToH264: boolean;
  enableWebRtcDebug: boolean;
  userManagementEnabled: boolean;
  errorDetectionEnabled: boolean;
  analyticsEnabled: boolean;
  enableSystemMonitor: boolean;
  enablePreview: boolean;
  recastLicensed: boolean;
  recordLicensed: boolean;
  rawPcapAvailable: boolean;
  udsDatagramAvailable: boolean;
  patternAvailable:boolean;
 patternUnavailableReason?:string;
 flywheelAvailable: boolean;
  flywheelLicensed: boolean;
  flywheelUnavailableReason: string | null;
  mapTileLayers: string | null;
};

export type AppContextValue = {
  serverConfig: ServerConfigState;
  iceServers: IceServers | null;
  forceRelay: boolean;
  iceServersLoading: boolean;
  iceServersError: string | null;
  refreshServerConfig: () => Promise<void>;
  analyticsEnabled: boolean;
  pwaNotifications: PwaNotificationSettings;
  pwaPushPublicKey: string | null;
  refreshPwaNotifications: (override?: PwaNotificationSettings) => Promise<PwaNotificationSettings | null>;
  connectedClients: number;
  eventStreamConnected: boolean;
  isDemoMode: boolean;
  isMaxClientsExceeded: boolean;
  connectionId: string | null;
};

const defaultServerConfig: ServerConfigState = {
  rtspPlaybackBaseUrl: null,
  iceServers: null,
  forceRelay: false,
  fullIce: false,
  keepHostCandidates: false,
  loopbackIceCandidate: false,
  nat1to1Enabled: false,
  publicIp: null,
  alwaysTranscodeH265ToH264: true,
  enableWebRtcDebug: false,
  userManagementEnabled: true,
  errorDetectionEnabled: true,
  analyticsEnabled: false,
  enableSystemMonitor: true,
  enablePreview: true,
  recastLicensed: false,
  recordLicensed: false,
  rawPcapAvailable: false,
  udsDatagramAvailable: false,
  patternAvailable:false,
 flywheelAvailable: false,
  flywheelLicensed: false,
  flywheelUnavailableReason: null,
  mapTileLayers: null,
};

const AppContext = createContext<AppContextValue | undefined>(undefined);

const normalizeIceServers = (value: unknown): IceServers | null => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value as IceServers;
  }
  return null;
};

const areIceServersEqual = (
  a: IceServers | null,
  b: IceServers | null,
): boolean => {
  if (a === b) {
    return true;
  }

  if (typeof a === "string" || typeof b === "string") {
    return a === b;
  }

  if (!Array.isArray(a) || !Array.isArray(b)) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  return a.every((server, index) => {
    const other = b[index];
    if (server === other) {
      return true;
    }
    return JSON.stringify(server) === JSON.stringify(other);
  });
};

const toBoolean = (value: unknown): boolean => value === true;

const toStringOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

const toNonNegativeInteger = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return Math.max(0, parsed);
    }
  }
  return null;
};

const isDesktopShellEnvironment = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const hasWailsRuntime =
    typeof (window as { runtime?: unknown }).runtime !== "undefined" ||
    typeof (window as { go?: unknown }).go !== "undefined" ||
    typeof (window as { __WAILS__?: unknown }).__WAILS__ !== "undefined";
  return (
    hasWailsRuntime ||
    /wails|webview2|;\s*wv\)/i.test(userAgent) ||
    window.location.hostname === "wails.localhost"
  );
};

const isStreamStateValue = (value: string): value is StreamStateValue => {
  const normalized = value.trim().toLowerCase() as StreamStateValue;
  return (
    normalized === "online" ||
    normalized === "offline" ||
    normalized === "timeout" ||
    normalized === "unknown" ||
    normalized === "connecting" ||
    normalized === "error"
  );
};

const parseStreamStateValue = (value: unknown): StreamStateValue | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (isStreamStateValue(normalized)) {
    return normalized;
  }
  return null;
};

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const {
    authorizedFetch,
    resolveApiPath,
    applyUserManagementEnabled,
    user,
    tokens,
    userManagementEnabled,
    userManagementResolved,
  } = useAuth();
  const requiresAuth = Boolean(userManagementEnabled);
  const authTokenRef = useRef<string>("");
  const latestAuthToken =
    requiresAuth && typeof tokens?.accessToken === "string"
      ? tokens.accessToken.trim()
      : "";
  const hasAuthToken = latestAuthToken.length > 0;
  const mayAccessProtectedApis = canAccessProtectedApis(
    userManagementResolved,
    userManagementEnabled,
    latestAuthToken,
  );
  authTokenRef.current = latestAuthToken;
  const desktopShell = useMemo(() => isDesktopShellEnvironment(), []);
  const desktopBackendAddrRef = useRef<string | null>(null);
  const [serverConfig, setServerConfig] = useState<ServerConfigState>(
    defaultServerConfig,
  );
  const [pwaNotifications, setPwaNotifications] = useState<PwaNotificationSettings>(
    defaultPwaNotificationSettings,
  );
  const [pwaPushPublicKey, setPwaPushPublicKey] = useState<string | null>(null);

  const refreshPwaNotifications = useCallback(
    async (override?: PwaNotificationSettings) => {
      if (override) {
        setPwaNotifications(override);
        return override;
      }

      try {
        const response = await authorizedFetch("/api/config/pwa");
        const payload = (await response.json().catch(() => null)) as
          | {
            config?:
              | {
                notifications?: Partial<PwaNotificationSettings>;
              }
              | Partial<PwaNotificationSettings>;
            error?: string;
          }
          | null;

        if (!response.ok || !payload?.config) {
          throw new Error(payload?.error ?? "Failed to load notification preferences");
        }

        const source =
          (payload.config as { notifications?: Partial<PwaNotificationSettings> } | undefined)?.notifications ??
          (payload.config as Partial<PwaNotificationSettings>);
        const normalized = normalizePwaNotificationSettings(source);
        setPwaNotifications(normalized);
        const pushConfig = (payload.config as { push?: { publicKey?: unknown } } | undefined)?.push;
        if (typeof pushConfig?.publicKey === "string") {
          const trimmed = pushConfig.publicKey.trim();
          setPwaPushPublicKey(trimmed || null);
        }
        return normalized;
      } catch (error) {
        console.error("Failed to load PWA notification preferences", error);
        return null;
      }
    },
    [authorizedFetch],
  );

  const [iceServersLoading, setIceServersLoading] = useState(true);
  const [iceServersError, setIceServersError] = useState<string | null>(null);
  const [connectedClients, setConnectedClients] = useState(0);
  const [eventStreamConnected, setEventStreamConnected] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const isMountedRef = useRef(false);
  const hasReceivedInitialEventRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const healthCheckTimerRef = useRef<number | null>(null);
  const refreshServerConfigRef = useRef<() => Promise<void>>(async () => undefined);
  const refreshConnectedClientsRef = useRef<() => Promise<void>>(async () => undefined);
  const refreshDemoModeRef = useRef<() => Promise<void>>(async () => undefined);

  // Debug: track mounts/unmounts
  useEffect(() => {
    const mountId = Math.random().toString(36).substr(2, 9);
    console.log(`[AppProvider] MOUNTED with ID: ${mountId}`);
    return () => {
      console.log(`[AppProvider] UNMOUNTED with ID: ${mountId}`);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [configUpdatedOpen, setConfigUpdatedOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null);
  const [expiredModalOpen, setExpiredModalOpen] = useState(false);
  const [expiredModalMessage, setExpiredModalMessage] = useState<string | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isMaxClientsExceeded, setIsMaxClientsExceeded] = useState(false);
  const expiredModalRef = useRef<HTMLDivElement | null>(null);
  const maxClientsExceededRef = useRef(false);

  const applyPayload = useCallback((payload: ServerConfigPayload | null) => {
    if (!isMountedRef.current || !payload || typeof payload !== "object") {
      return false;
    }

    let didChange = false;
    let userManagementUpdate: boolean | null = null;

    // Handle demo mode status changes from backend
    if (Object.prototype.hasOwnProperty.call(payload, "isDemoMode")) {
      const normalized = toBoolean(payload.isDemoMode);
      setIsDemoMode((previous) => previous === normalized ? previous : normalized);
    }

    // Handle backend-driven expiration notifications
    if (Object.prototype.hasOwnProperty.call(payload, "licenseExpired") && payload.licenseExpired === true) {
      setExpiredModalMessage("License expired");
      setExpiredModalOpen(true);
    }
    if (Object.prototype.hasOwnProperty.call(payload, "demoExpired") && payload.demoExpired === true) {
      setExpiredModalMessage("Demo expired");
      setExpiredModalOpen(true);
    }
    if (Object.prototype.hasOwnProperty.call(payload, "maxClientsExceeded") && payload.maxClientsExceeded === true) {
      // Only set the flag once to prevent multiple modals
      if (!maxClientsExceededRef.current) {
        maxClientsExceededRef.current = true;
        setExpiredModalMessage("Maximum clients exceeded");
        setExpiredModalOpen(true);
        setIsMaxClientsExceeded(true);
      }
      // Close the WebSocket connection from client side
      if (wsRef.current) {
        wsRef.current.close();
      }
    }

    setServerConfig((prev) => {
      const next: ServerConfigState = { ...prev };

      if (Object.prototype.hasOwnProperty.call(payload, "rtspPlaybackBaseUrl")) {
        const normalized = toStringOrNull(payload.rtspPlaybackBaseUrl);
        if (normalized !== prev.rtspPlaybackBaseUrl) didChange = true;
        next.rtspPlaybackBaseUrl = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "iceServers")) {
        const normalized = normalizeIceServers(payload.iceServers);
        if (!areIceServersEqual(normalized, prev.iceServers)) {
          didChange = true;
        }
        next.iceServers = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "forceRelay")) {
        const normalized = toBoolean(payload.forceRelay);
        if (normalized !== prev.forceRelay) {
          didChange = true;
        }
        next.forceRelay = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "fullIce")) {
        const normalized = toBoolean(payload.fullIce);
        if (normalized !== prev.fullIce) {
          didChange = true;
        }
        next.fullIce = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "keepHostCandidates")) {
        const normalized = toBoolean(payload.keepHostCandidates);
        if (normalized !== prev.keepHostCandidates) {
          didChange = true;
        }
        next.keepHostCandidates = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "loopbackIceCandidate")) {
        const normalized = toBoolean(payload.loopbackIceCandidate);
        if (normalized !== prev.loopbackIceCandidate) {
          didChange = true;
        }
        next.loopbackIceCandidate = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "nat1to1Enabled")) {
        const normalized = toBoolean(payload.nat1to1Enabled);
        if (normalized !== prev.nat1to1Enabled) {
          didChange = true;
        }
        next.nat1to1Enabled = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "publicIp")) {
        const normalized = toStringOrNull(payload.publicIp);
        if (normalized !== prev.publicIp) {
          didChange = true;
        }
        next.publicIp = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "alwaysTranscodeH265ToH264")) {
        const normalized = toBoolean(payload.alwaysTranscodeH265ToH264);
        if (normalized !== prev.alwaysTranscodeH265ToH264) {
          didChange = true;
        }
        next.alwaysTranscodeH265ToH264 = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "mapTileLayers")) {
        const normalized = toStringOrNull(payload.mapTileLayers);
        if (normalized !== prev.mapTileLayers) {
          didChange = true;
        }
        next.mapTileLayers = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "analyticsEnabled")) {
        const normalized = toBoolean(payload.analyticsEnabled);
        if (normalized !== prev.analyticsEnabled) {
          didChange = true;
        }
        next.analyticsEnabled = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "enableSystemMonitor")) {
        const normalized = toBoolean(payload.enableSystemMonitor);
        if (normalized !== prev.enableSystemMonitor) {
          didChange = true;
        }
        next.enableSystemMonitor = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "enablePreview")) {
        const normalized = toBoolean(payload.enablePreview);
        if (normalized !== prev.enablePreview) {
          didChange = true;
        }
        next.enablePreview = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "recastLicensed")) {
        const normalized = toBoolean(payload.recastLicensed);
        if (normalized !== prev.recastLicensed) didChange = true;
        next.recastLicensed = normalized;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "recordLicensed")) {
        const normalized = toBoolean(payload.recordLicensed);
        if (normalized !== prev.recordLicensed) didChange = true;
        next.recordLicensed = normalized;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "rawPcapAvailable")) {
        const normalized = toBoolean(payload.rawPcapAvailable);
        if (normalized !== prev.rawPcapAvailable) didChange = true;
        next.rawPcapAvailable = normalized;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "udsDatagramAvailable")) {
        const normalized = toBoolean(payload.udsDatagramAvailable);
        if (normalized !== prev.udsDatagramAvailable) didChange = true;
        next.udsDatagramAvailable = normalized;
      }
      if (Object.prototype.hasOwnProperty.call(payload,"patternAvailable")) {
        const available=toBoolean(payload.patternAvailable);if(available!==prev.patternAvailable)didChange=true;next.patternAvailable=available;
      }
      if (Object.prototype.hasOwnProperty.call(payload,"patternUnavailableReason")) {
        const reason=String(payload.patternUnavailableReason ?? "");if(reason!==prev.patternUnavailableReason)didChange=true;next.patternUnavailableReason=reason;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "flywheelAvailable")) {
        const normalized = toBoolean(payload.flywheelAvailable);
        if (normalized !== prev.flywheelAvailable) didChange = true;
        next.flywheelAvailable = normalized;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "flywheelLicensed")) {
        const normalized = toBoolean(payload.flywheelLicensed);
        if (normalized !== prev.flywheelLicensed) didChange = true;
        next.flywheelLicensed = normalized;
      }
      if (Object.prototype.hasOwnProperty.call(payload, "flywheelUnavailableReason")) {
        const normalized = toStringOrNull(payload.flywheelUnavailableReason);
        if (normalized !== prev.flywheelUnavailableReason) didChange = true;
        next.flywheelUnavailableReason = normalized;
      }


      if (Object.prototype.hasOwnProperty.call(payload, "enableWebRtcDebug")) {
        const normalized = toBoolean(payload.enableWebRtcDebug);
        if (normalized !== prev.enableWebRtcDebug) {
          didChange = true;
        }
        next.enableWebRtcDebug = normalized;
      }

      if (
        Object.prototype.hasOwnProperty.call(payload, "userManagementEnabled") ||
        Object.prototype.hasOwnProperty.call(payload, "userManagementDisabled")
      ) {
        let normalized = prev.userManagementEnabled;
        if (Object.prototype.hasOwnProperty.call(payload, "userManagementEnabled")) {
          normalized = toBoolean(payload.userManagementEnabled);
        } else if (Object.prototype.hasOwnProperty.call(payload, "userManagementDisabled")) {
          normalized = !toBoolean(payload.userManagementDisabled);
        }
        if (normalized !== prev.userManagementEnabled) {
          didChange = true;
          // Store the update to apply after state update completes
          userManagementUpdate = normalized;
        }
        next.userManagementEnabled = normalized;
      }

      if (Object.prototype.hasOwnProperty.call(payload, "errorDetectionEnabled")) {
        const normalized = toBoolean(payload.errorDetectionEnabled);
        if (normalized !== prev.errorDetectionEnabled) {
          didChange = true;
        }
        next.errorDetectionEnabled = normalized;
      }

      if (!didChange) {
        return prev;
      }

      return next;
    });

    // Apply user management state update outside of render cycle
    if (userManagementUpdate !== null) {
      // Use setTimeout to defer the state update to after render
      setTimeout(() => {
        if (userManagementUpdate !== null) {
          applyUserManagementEnabled(userManagementUpdate);
        }
      }, 0);
    }

    return didChange;
  }, [applyUserManagementEnabled]);

  const refreshServerConfig = useCallback(async () => {
    try {
      const response = await authorizedFetch("/api/serverConfig", {
        cache: "no-store",
      });
      const payload = (await response.json().catch(() => null)) as
        | (ServerConfigPayload & { error?: string })
        | null;

      if (!response.ok) {
        throw new Error(
          payload?.error ?? `Failed to load server configuration (${response.status})`,
        );
      }

      applyPayload(payload);
      if (isMountedRef.current) {
        setIceServersError(null);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setIceServersError(
        error instanceof Error
          ? error.message
          : "Unable to load server configuration",
      );
    }
  }, [applyPayload, authorizedFetch]);

  const refreshConnectedClients = useCallback(async () => {
    if (!mayAccessProtectedApis) {
      return;
    }

    try {
      const response = await authorizedFetch(
        "/api/clients/count",
      );
      const payload = (await response.json().catch(() => null)) as
        | { connectedClients?: unknown; error?: string }
        | null;

      if (!response.ok) {
        throw new Error(
          payload?.error ??
          `Failed to load connected client count (${response.status})`,
        );
      }

      const normalized = payload
        ? toNonNegativeInteger(payload.connectedClients)
        : null;
      if (normalized !== null && isMountedRef.current) {
        setConnectedClients(normalized);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error("Failed to load connected client count", error);
      }
    }
  }, [authorizedFetch, mayAccessProtectedApis]);

  const refreshDemoMode = useCallback(async () => {
    if (!mayAccessProtectedApis) {
      return;
    }

    try {
      const response = await authorizedFetch("/api/config/license");
      const payload = (await response.json().catch(() => null)) as
        | { isDemoMode?: unknown; error?: string }
        | null;

      if (!response.ok) {
        throw new Error(
          payload?.error ?? `Failed to load license info (${response.status})`,
        );
      }

      if (
        payload &&
        typeof payload.isDemoMode === "boolean" &&
        isMountedRef.current
      ) {
        setIsDemoMode(payload.isDemoMode);
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error("Failed to load demo mode status", error);
      }
    }
  }, [authorizedFetch, mayAccessProtectedApis]);

  useEffect(() => {
    refreshServerConfigRef.current = refreshServerConfig;
  }, [refreshServerConfig]);

  useEffect(() => {
    refreshConnectedClientsRef.current = refreshConnectedClients;
  }, [refreshConnectedClients]);

  useEffect(() => {
    refreshDemoModeRef.current = refreshDemoMode;
  }, [refreshDemoMode]);

  const resolveDesktopBackendAddr = useCallback(async (): Promise<string | null> => {
    if (!desktopShell) {
      return null;
    }
    if (desktopBackendAddrRef.current) {
      return desktopBackendAddrRef.current;
    }
    try {
      const response = await fetch("/__desktop_meta", { cache: "no-store" });
      const payload = (await response.json().catch(() => null)) as { backendAddr?: unknown } | null;
      if (!response.ok) {
        return null;
      }
      const backendAddr =
        typeof payload?.backendAddr === "string" ? payload.backendAddr.trim() : "";
      if (!backendAddr) {
        return null;
      }
      desktopBackendAddrRef.current = backendAddr;
      return backendAddr;
    } catch {
      return null;
    }
  }, [desktopShell]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      await refreshServerConfig();
      if (!cancelled && isMountedRef.current) {
        setIceServersLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [refreshServerConfig]);

  useEffect(() => {
    if (!userManagementResolved || (requiresAuth && !hasAuthToken)) {
      return;
    }
    void refreshPwaNotifications();
  }, [hasAuthToken, refreshPwaNotifications, requiresAuth, userManagementResolved]);

  useEffect(() => {
    if (!mayAccessProtectedApis) {
      setConnectedClients(0);
      return;
    }
    void refreshConnectedClients();
  }, [mayAccessProtectedApis, refreshConnectedClients]);

  useEffect(() => {
    if (!mayAccessProtectedApis) {
      return;
    }
    void refreshDemoMode();
  }, [mayAccessProtectedApis, refreshDemoMode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      if (healthCheckTimerRef.current !== null) {
        window.clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const isWebSocketOpen = () =>
      typeof WebSocket !== "undefined" &&
      wsRef.current?.readyState === WebSocket.OPEN;

    const checkHealth = async () => {
      try {
        const response = await fetch(resolveApiPath("/api/health"), {
          cache: "no-store",
        });
        if (!cancelled && isMountedRef.current) {
          if (response.ok) {
            setEventStreamConnected(true);
          } else if (!isWebSocketOpen()) {
            setEventStreamConnected(false);
          }
        }
      } catch (error) {
        if (!cancelled && isMountedRef.current && !isWebSocketOpen()) {
          setEventStreamConnected(false);
        }
      }
    };

    void checkHealth();
    healthCheckTimerRef.current = window.setInterval(checkHealth, 5000);

    return () => {
      cancelled = true;
      if (healthCheckTimerRef.current !== null) {
        window.clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
    };
  }, [resolveApiPath]);

  useEffect(() => {
    console.log("[AppContext] WebSocket useEffect triggered");
    if (typeof window === "undefined") {
      return;
    }

    if (typeof WebSocket === "undefined") {
      if (isMountedRef.current) {
        setIceServersError(
          "Live server configuration updates are not supported in this environment.",
        );
      }
      return;
    }

    function clearReconnectTimer() {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    function handleMessage(event: MessageEvent) {
      try {
        const envelope = JSON.parse(event.data) as { type: string; data: unknown } | null;
        if (!envelope || typeof envelope.type !== "string") {
          return;
        }

        if (envelope.type === serverConfigChangedEvent) {
          const data = envelope.data as ServerConfigPayload | null;
          const changed = applyPayload(data);
          if (isMountedRef.current) {
            setEventStreamConnected(true);
            setIceServersError(null);
            setIceServersLoading(false);
            if (hasReceivedInitialEventRef.current) {
              if (changed) {
                setConfigUpdatedOpen(true);
              }
            } else {
              hasReceivedInitialEventRef.current = true;
            }
          }
        } else if (envelope.type === clientCountChangedEvent) {
          const data = envelope.data as { connectedClients?: unknown } | null;
          const normalized = data ? toNonNegativeInteger(data.connectedClients) : null;
          if (normalized !== null && isMountedRef.current) {
            setConnectedClients(normalized);
          }
        } else if (envelope.type === streamStateSnapshotEvent || envelope.type === streamStateChangedEvent) {
          const message: StreamStateMessage = {
            type: envelope.type as typeof streamStateSnapshotEvent | typeof streamStateChangedEvent,
            data: envelope.data,
          };
          streamStateSubscribers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error("Error in stream state message handler:", error);
            }
          });
        } else if (envelope.type === streamOutputStateSnapshotEvent || envelope.type === streamOutputStateChangedEvent) {
          const message: StreamOutputStateMessage = {
            type: envelope.type as typeof streamOutputStateSnapshotEvent | typeof streamOutputStateChangedEvent,
            data: envelope.data,
          };
          streamOutputStateSubscribers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error("Error in stream output state message handler:", error);
            }
          });
        } else if (envelope.type === streamDetectionEvent) {
          const message: StreamDetectionMessage = {
            type: envelope.type,
            data: envelope.data,
          };
          streamDetectionSubscribers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error("Error in stream detection message handler:", error);
            }
          });
        } else if (envelope.type === streamListChangedEvent) {
          const message: StreamListChangeMessage = {
            type: envelope.type,
            data: envelope.data,
          };
          streamListChangeSubscribers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error("Error in stream list change message handler:", error);
            }
          });
        } else if (envelope.type === platformListChangedEvent) {
          const message: PlatformListChangeMessage = {
            type: envelope.type,
            data: envelope.data,
          };
          platformListChangeSubscribers.forEach(handler => {
            try {
              handler(message);
            } catch (error) {
              console.error("Error in platform list change message handler:", error);
            }
          });
        } else if (envelope.type === notificationEvent) {
          const raw = (envelope.data ?? {}) as { message?: unknown };
          const message =
            typeof raw.message === "string" && raw.message.trim().length > 0
              ? raw.message.trim()
              : null;
          if (message && isMountedRef.current) {
            setNotificationMessage(message);
            setNotificationOpen(true);
            const notification: NotificationMessage = {
              type: notificationEvent,
              data: { message },
            };
            notificationSubscribers.forEach((handler) => {
              try {
                handler(notification);
              } catch (error) {
                console.error("Error in notification handler:", error);
              }
            });
          }
        } else if (envelope.type === connectionInfoEvent) {
          const raw = (envelope.data ?? {}) as { connectionId?: unknown };
          const id =
            typeof raw.connectionId === "string" && raw.connectionId.trim().length > 0
              ? raw.connectionId.trim()
              : null;
          if (isMountedRef.current) {
            setConnectionId(id);
          }
        } else if (envelope.type === offlineMapProgressEvent) {
          const message: OfflineMapProgressMessage = {
            type: offlineMapProgressEvent,
            data: envelope.data ?? {},
          };
          offlineMapProgressSubscribers.forEach((handler) => {
            try {
              handler(message);
            } catch (error) {
              console.error("Error in offline map progress handler:", error);
            }
          });
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message", error);
      }
    }

    function cleanupWebSocket() {
      if (wsRef.current) {
        const connectionId = (wsRef.current as any)?._connectionId;
        console.log(`[AppContext] Cleaning up WebSocket connection ${connectionId}`);
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      if (isMountedRef.current) {
        setConnectionId(null);
      }
    }

    function scheduleReconnect() {
      if (!isMountedRef.current) {
        return;
      }
      if (maxClientsExceededRef.current) {
        console.log("[AppContext] Not reconnecting - max clients exceeded");
        return;
      }
      if (reconnectTimerRef.current !== null) {
        return;
      }
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void connect();
      }, 1500);
    }

    async function connect() {
      if (wsRef.current) {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          console.log("[AppContext] WebSocket already exists, skipping connection creation");
          return;
        }
        cleanupWebSocket();
      }
      hasReceivedInitialEventRef.current = false;

      try {
        setConnectionId(null);
        let wsUrl: string;
        if (desktopShell) {
          const desktopBackendAddr = await resolveDesktopBackendAddr();
          if (desktopBackendAddr) {
            wsUrl = `ws://${desktopBackendAddr}/api/ws`;
          } else {
            const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
            const host = window.location.host;
            const apiPath = resolveApiPath("/api/ws");
            wsUrl = apiPath.startsWith("http://") || apiPath.startsWith("https://")
              ? apiPath.replace(/^http/, "ws")
              : `${protocol}//${host}${apiPath}`;
          }
        } else {
          const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
          const host = window.location.host;
          const apiPath = resolveApiPath("/api/ws");
          wsUrl = apiPath.startsWith("http://") || apiPath.startsWith("https://")
            ? apiPath.replace(/^http/, "ws")
            : `${protocol}//${host}${apiPath}`;
        }

        const authToken = requiresAuth ? authTokenRef.current : "";
        if (authToken) {
          const separator = wsUrl.includes("?") ? "&" : "?";
          const encodedToken = encodeURIComponent(authToken);
          wsUrl = `${wsUrl}${separator}access_token=${encodedToken}`;
        }

        const connectionId = Math.random().toString(36).substr(2, 9);
        const logUrl =
          authToken && wsUrl.includes("access_token=")
            ? wsUrl.replace(encodeURIComponent(authToken), "***")
            : wsUrl;
        console.log(`[AppContext] Creating WebSocket connection ${connectionId} to ${logUrl}`);

        wsRef.current = new WebSocket(wsUrl);

        console.log(`[AppContext] WebSocket connection ${connectionId} created`);

        (wsRef.current as any)._connectionId = connectionId;
      } catch (error) {
        console.error("Unable to connect to WebSocket", error);
        if (isMountedRef.current) {
          setIceServersError("Unable to subscribe to server updates");
          setEventStreamConnected(false);
        }
        if (desktopShell) {
          desktopBackendAddrRef.current = null;
        }
        scheduleReconnect();
        return;
      }

      wsRef.current.onopen = () => {
        const connectionId = (wsRef.current as any)?._connectionId;
        console.log(`[AppContext] WebSocket connection ${connectionId} opened`);
        clearReconnectTimer();
        if (isMountedRef.current) {
          setEventStreamConnected(true);
          setIceServersError(null);
        }
        void refreshServerConfigRef.current();
        void refreshConnectedClientsRef.current();
        void refreshDemoModeRef.current();
      };

      wsRef.current.onmessage = handleMessage;

      wsRef.current.onerror = (event) => {
        const connectionId = (wsRef.current as any)?._connectionId;
        const readyState = wsRef.current?.readyState;
        console.log(
          `[AppContext] WebSocket connection ${connectionId} error (readyState=${readyState})`,
          event,
        );
        if (isMountedRef.current) {
          setIceServersError((prev) => prev ?? "Connection to server updates lost");
          setEventStreamConnected(false);
          setConnectionId(null);
        }
        if (desktopShell) {
          desktopBackendAddrRef.current = null;
        }
        cleanupWebSocket();
        scheduleReconnect();
      };

      wsRef.current.onclose = (event) => {
        const connectionId = (wsRef.current as any)?._connectionId;
        console.log(
          `[AppContext] WebSocket connection ${connectionId} closed (code=${event.code}, reason="${event.reason}", clean=${event.wasClean})`,
        );
        if (isMountedRef.current) {
          setEventStreamConnected(false);
          setConnectionId(null);
        }
        if (desktopShell) {
          desktopBackendAddrRef.current = null;
        }
        scheduleReconnect();
      };
    }

    if (!mayAccessProtectedApis) {
      clearReconnectTimer();
      cleanupWebSocket();
      if (isMountedRef.current) {
        setEventStreamConnected(false);
      }
      return () => {
        clearReconnectTimer();
        cleanupWebSocket();
      };
    }

    void connect();

    return () => {
      clearReconnectTimer();
      cleanupWebSocket();
      if (isMountedRef.current) {
        setEventStreamConnected(false);
      }
    };
  }, [
    resolveApiPath,
    desktopShell,
    resolveDesktopBackendAddr,
    hasAuthToken,
    userManagementEnabled,
    mayAccessProtectedApis,
  ]);

  useEffect(() => {
    if (expiredModalOpen && expiredModalRef.current) {
      // focus the content so that onBlur will fire when focus leaves
      expiredModalRef.current.focus();
    }
  }, [expiredModalOpen]);

  useEffect(() => {
    if (eventStreamConnected && notificationMessage === restartNotificationMessage) {
      setNotificationOpen(false);
      setNotificationMessage(null);
    }
  }, [eventStreamConnected, notificationMessage]);

  const value = useMemo<AppContextValue>(
    () => ({
      serverConfig,
      iceServers: serverConfig.iceServers,
      forceRelay: serverConfig.forceRelay,
      iceServersLoading,
      iceServersError,
      refreshServerConfig,
      pwaNotifications,
      pwaPushPublicKey,
      refreshPwaNotifications,
      connectedClients,
      eventStreamConnected,
      isDemoMode,
      isMaxClientsExceeded,
      connectionId,
      analyticsEnabled: serverConfig.analyticsEnabled,
    }),
    [
      serverConfig,
      iceServersError,
      iceServersLoading,
      refreshServerConfig,
      pwaNotifications,
      pwaPushPublicKey,
      refreshPwaNotifications,
      connectedClients,
      eventStreamConnected,
      isDemoMode,
      isMaxClientsExceeded,
      connectionId,
    ],
  );

  const handleSnackbarClose = useCallback(
    (_: SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason === "clickaway") {
        return;
      }
      setConfigUpdatedOpen(false);
    },
    []
  );

  return (
    <>
      <AppContext.Provider value={value}>{children}</AppContext.Provider>
      <Snackbar
        open={configUpdatedOpen}
        message="Server configuration updated"
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <Snackbar
        open={notificationOpen}
        message={notificationMessage ?? ""}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === "clickaway") {
            return;
          }
          setNotificationOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onExited={() => {
          setNotificationMessage(null);
        }}
      />
      <Modal open={expiredModalOpen} onClose={() => setExpiredModalOpen(false)}>
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <Fade in={expiredModalOpen} timeout={1000}>
            <Box
              ref={expiredModalRef}
              tabIndex={-1}
              onBlur={() => setExpiredModalOpen(false)}
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                p: 3,
                minHeight: 128,
                width: "min(500px, 95vw)",
                boxShadow: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 1,
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.28),
              }}
            >
              ⚠️
              <Typography sx={{ color: (theme) => theme.palette.error.main }} variant="h6">
                {(expiredModalMessage ?? "Session expired").toUpperCase()}
              </Typography>
            </Box>
          </Fade>
        </Box>
      </Modal>
    </>
  );
};

export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
